import { useRef, useState, useMemo, useEffect, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useClientContextStore } from '@/store/clientContext'
import { fetchClientOptionsList } from '@/lib/services/clientContextService'
import { downloadStoresTemplate, exportStoresToCSV, importStores } from '@/lib/services/storeService'

type TabValue = 'all' | 'trash'

export default function StoresPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const activeClientCode = useClientContextStore((s) => s.activeClientCode)
  const setActiveClient = useClientContextStore((s) => s.setActiveClient)

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const [clientOptions, setClientOptions] = useState<Array<{ id: number; code: string; name: string; database_name?: string }>>([])

  const access = useAccessCheck()

  useEffect(() => {
    fetchClientOptionsList()
      .then((clients) => {
        setClientOptions(
          clients.map((c) => ({
            id: c.id,
            code: c.code,
            name: c.name,
            database_name: c.database_name,
          }))
        )
      })
      .catch(() => {
        setClientOptions([])
      })
  }, [])

  const statusFilterOptions = useMemo(
    () => [
      { value: 'Active', label: 'Active' },
      { value: 'Inactive', label: 'Inactive' },
    ],
    []
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        branch_name: {
          name: 'Branch Name',
          withSort: true,
          maxWidth: '220px',
        },
        branch_code: {
          name: 'Branch Code',
          withSort: true,
          maxWidth: '140px',
        },
        business_unit_name: {
          name: 'Business Unit',
          withSort: false,
          maxWidth: '180px',
        },
        active: {
          name: 'Status',
          withSort: true,
          withFilter: true,
          filterOptions: statusFilterOptions,
          badge: {
            Active: 'green',
            Inactive: 'yellow',
          },
          badgeLabels: {
            Active: 'Active',
            Inactive: 'Inactive',
          },
        },
        updated_at: {
          name: 'Updated At',
          withSort: true,
          maxWidth: '160px',
        },
      },
      dataSource:
        activeTab === 'trash'
          ? '/store-management/archived/stores'
          : '/store-management/stores',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: activeClientCode ? 'No stores found' : 'Select a client to load stores',
      otherActions: [
        {
          name: 'View Details',
          onClick: (row) => navigate(`/organization/stores/${row.id}/details`),
          show: () => true,
        },
      ],
    }),
    [activeTab, activeClientCode, statusFilterOptions, navigate]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const tableParams = useMemo(
    () => ({ search: searchQuery, _client: activeClientCode ?? '' }),
    [searchQuery, activeClientCode]
  )

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleBulkActionClick = () => {
    if (!activeClientCode) {
      showWarning('Select a client first')
      return
    }
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }

    if (bulkAction === 'delete' && activeTab === 'trash') {
      setModalMessage('You are about to permanently delete these stores. This action cannot be undone.')
    } else {
      setModalMessage('Are you sure to apply this change?')
    }
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      let url = ''
      const payload = { ids: selectedIds }
      let successMessage = 'Action completed successfully'

      if (bulkAction === 'restore') {
        url = '/store-management/stores/bulk/restore'
        successMessage = 'Stores restored successfully'
      } else if (bulkAction === 'delete') {
        url =
          activeTab === 'trash'
            ? '/store-management/stores/bulk/force-delete'
            : '/store-management/stores/bulk/delete'
        successMessage =
          activeTab === 'trash'
            ? 'Stores permanently deleted successfully'
            : 'Stores moved to trash successfully'
      }

      await apiClient.post(url, payload)
      showSuccess('Success', successMessage)
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: unknown) {
      showError(error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const handleDownloadTemplate = () => {
    downloadStoresTemplate()
    showSuccess('Success', 'Template downloaded')
  }

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!activeClientCode) {
      showWarning('Select a client before importing')
      return
    }

    setIsImporting(true)
    try {
      const result = await importStores(file)
      const imported = result?.imported ?? result?.success_count ?? 0
      const errors: string[] = result?.errors ?? []
      showSuccess('Success', `Imported ${imported} store(s)`)
      if (errors.length) {
        showWarning(errors.slice(0, 3).join('; ') + (errors.length > 3 ? '...' : ''))
      }
      tableRef.current?.reload()
    } catch (err: unknown) {
      showError(err)
    } finally {
      setIsImporting(false)
      e.target.value = ''
    }
  }

  const handleExportData = async () => {
    if (!activeClientCode) {
      showWarning('Select a client before exporting')
      return
    }

    setIsExporting(true)
    try {
      await exportStoresToCSV({
        activeTab,
        clientCode: activeClientCode,
        showError,
        showSuccess,
        showWarning: (title, message) => showWarning(message ?? title),
      })
    } catch (error: unknown) {
      showError(error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Stores
              </Heading>
              <Text fontSize="sm" color={colors.text} mb={2}>
                Manage branch stores for the selected client database
              </Text>
              <HStack spacing={3} align="center" flexWrap="wrap">
                <Text fontSize="sm" fontWeight="medium" color={colors.text}>
                  Business Unit (Client)
                </Text>
                <Select
                  size="sm"
                  maxW="360px"
                  placeholder="Select client"
                  value={activeClientCode ?? ''}
                  onChange={(e) => {
                    const code = e.target.value
                    const selected = clientOptions.find((c) => c.code === code)
                    setActiveClient(
                      selected
                        ? {
                            id: selected.id,
                            code: selected.code,
                            name: selected.name,
                            database_name: selected.database_name,
                          }
                        : null
                    )
                  }}
                >
                  {clientOptions.map((client) => (
                    <option key={client.id} value={client.code}>
                      {client.name} ({client.code})
                    </option>
                  ))}
                </Select>
              </HStack>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRows}
                        </Badge>
                      </Tab>
                      {totalTrash > 0 && (
                        <Tab>
                          Trash{' '}
                          <Badge ml={2} colorScheme="red">
                            {totalTrash}
                          </Badge>
                        </Tab>
                      )}
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      pathname="/organization/stores"
                      onExport={handleExportData}
                      onDownloadTemplate={handleDownloadTemplate}
                      createLabel="Create Store"
                      isExporting={isExporting}
                      isImporting={isImporting}
                      fileInputRef={fileInputRef}
                      onFileUpload={handleFileUpload}
                      acceptFileTypes=".csv"
                    />
                  </Box>
                </HStack>
                <Box px={4} pt={2}>
                  <Text fontSize="xs" color={colors.text}>
                    Import Format v2: Required columns are
                    {' '}
                    <strong>
                      branch_name, business_unit, branch_code, branch_address, branch_email, latitude, longitude, active
                    </strong>
                    . Include at least one of <strong>contact_number</strong> or <strong>mobile_number</strong>.
                    Optional columns: <strong>region</strong>, <strong>province</strong>, <strong>contact_person</strong>,
                    {' '}
                    <strong>location_address</strong>, <strong>normalized_address</strong>.
                  </Text>
                </Box>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        e.stopPropagation()
                        setBulkAction(e.target.value)
                      }}
                      isDisabled={!activeClientCode}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick} isDisabled={!activeClientCode}>
                      Apply
                    </Button>
                  </HStack>
                )}

                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by branch name or code..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                      isDisabled={!activeClientCode}
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                        isDisabled={!activeClientCode}
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                {activeClientCode ? (
                  <DataTable
                    key={activeClientCode + activeTab}
                    ref={tableRef}
                    options={tableOptions}
                    params={tableParams}
                    access={permissions}
                    setSubSub={showSubSubCallback}
                    onOpenModal={(id) => navigate(`/organization/stores/${id}`)}
                    filterOptions={{
                      active: statusFilterOptions,
                    }}
                  />
                ) : (
                  <Box py={12} textAlign="center" color={colors.text}>
                    <Text>No client selected.</Text>
                  </Box>
                )}
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBulkProcessing}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirmBulkAction}
              isLoading={isBulkProcessing}
              loadingText="Processing..."
            >
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
